/**
 * Minimal data hook for customer screens. The in-memory cache is tenant data: resetResourceCache() is
 * wired into clearDeviceData (sign-out / workspace switch) so nothing from a previous tenant renders.
 */
import { useCallback, useEffect, useState } from 'react';
import { bff, type BffError } from './bff';

const memory = new Map<string, unknown>();
export function resetResourceCache(): void { memory.clear(); }
export const resourceCacheSize = () => memory.size;

export type ResourceState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: BffError | Error };

export function useResource<T>(path: `/v1/${string}` | null): ResourceState<T> & { reload: () => void } {
  const cached = path ? (memory.get(path) as T | undefined) : undefined;
  const [state, setState] = useState<ResourceState<T>>(cached !== undefined ? { status: 'ready', data: cached, error: null } : { status: 'loading', data: null, error: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    bff<T>(path).then(
      (data) => { memory.set(path, data); if (live) setState({ status: 'ready', data, error: null }); },
      (error: Error) => { if (live) setState({ status: 'error', data: null, error }); },
    );
    return () => { live = false; };
  }, [path, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, reload };
}
